import { createRoute } from '@tanstack/react-router'

import { areaRoute } from '@/app/routes/area-layout'
import { lazyPage } from '@/lib/lazy-page'
import { booleanParam } from '@/lib/search-params'
import {
  auditLogGridContract,
  normalizeDataGridUrlState,
  type ColumnId,
} from '@/modules/data-grid'

export type AccountAuditLogSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
  includeDescendants?: boolean
  includeDerived?: boolean
}

const BOOKMARKED_SCOPE_KEYS = new Set(['children', 'derived'])
export const CHOOSER_COLUMNS: readonly ColumnId<typeof auditLogGridContract>[] = [
  'timestamp', 'action', 'subjectType', 'entity', 'details', 'actor',
]

export function decodeAccountAuditLogSearch(
  search: Record<string, unknown>,
): AccountAuditLogSearch {
  return normalizeDataGridUrlState({
    ...search,
    ...scopeFromBookmarkedKeys(search),
    columns: chooserColumns(search.columns),
  }, auditLogGridContract)
}

function scopeFromBookmarkedKeys(search: Record<string, unknown>) {
  return {
    includeDescendants: search.includeDescendants
      ?? (booleanParam(search.children) === false ? false : undefined),
    includeDerived: search.includeDerived
      ?? (booleanParam(search.derived) === true ? true : undefined),
  }
}

function chooserColumns(raw: unknown) {
  return typeof raw === 'string'
    ? raw.split(',').filter((column) => (CHOOSER_COLUMNS as readonly string[]).includes(column)).join(',')
    : undefined
}

export const AccountAuditLog = lazyPage(() => import('./features/account-audit-log/account-audit-log.lazy'), 'AccountAuditLog')

export const accountAuditLogRoute = createRoute({
  getParentRoute: () => areaRoute('access'),
  path: 'audit-log',
  validateSearch: decodeAccountAuditLogSearch,
  search: {
    middlewares: [({ search, next }) => Object.fromEntries(
      Object.entries(next(search)).filter(([key]) => !BOOKMARKED_SCOPE_KEYS.has(key)),
    ) as AccountAuditLogSearch],
  },
  component: AccountAuditLog,
})
